"use client";

import React from "react";
import Image from "next/image";
import { Plus, MessageSquare, Trash2, History } from "lucide-react";
import type { StoredChat } from "@/types";

interface SidebarProps {
  chats: StoredChat[];
  currentChatId: string | null;
  onNewChat: () => void;
  onSelectChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
  isOpen: boolean;
}

export function Sidebar({
  chats,
  currentChatId,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  isOpen,
}: SidebarProps) {
  if (!isOpen) return null;

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return "اليوم";
    if (days === 1) return "أمس";
    if (days < 7) return `منذ ${days} أيام`;
    return date.toLocaleDateString("ar");
  };

  return (
    <div className="w-72 h-screen flex flex-col text-white" style={{ backgroundColor: "#0b015d" }}>
      {/* Header with Logo */}
      <div className="p-4 border-b border-white/20">
        <div className="flex justify-center mb-4">
          <Image
            src="/mdarjLogoAR.svg"
            alt="MDARJ"
            width={120}
            height={60}
            className="brightness-0 invert"
          />
        </div>
        <button
          onClick={onNewChat}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all text-white font-medium"
          style={{ backgroundColor: "#d3a035" }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
          onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
        >
          <Plus size={20} />
          <span>محادثة جديدة</span>
        </button>
      </div>

      {/* Chat History */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex items-center gap-2 text-white/60 text-sm mb-3">
          <History size={16} />
          <span>سجل المحادثات</span>
        </div>

        {chats.length === 0 ? (
          <div className="text-white/40 text-sm text-center py-8">
            لا توجد محادثات بعد
          </div>
        ) : (
          <div className="space-y-2">
            {chats.map((chat) => (
              <div
                key={chat.id}
                className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${
                  currentChatId === chat.id
                    ? "bg-white/20"
                    : "hover:bg-white/10"
                }`}
                onClick={() => onSelectChat(chat.id)}
                dir="rtl"
              >
                <MessageSquare size={16} className="shrink-0 text-white/60" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{chat.title}</p>
                  <p className="text-xs text-white/40">
                    {formatDate(chat.updatedAt)}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteChat(chat.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/20 rounded transition-all"
                  title="حذف المحادثة"
                >
                  <Trash2 size={14} className="text-white/60" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-white/20">
        <div className="text-center text-xs text-white/40">
          مساعد MDARJ الصوتي
        </div>
      </div>
    </div>
  );
}
